import React from "react"
import {Survicate} from "@survicate/survicate-web-surveys-wrapper"
import {getSurvicateInstance, initSurvicate} from "@survicate/survicate-web-surveys-wrapper/widget_wrapper"
import {QueryClient, QueryKey} from "@tanstack/react-query"
import axios from "axios"
import Rollbar from "rollbar"

import {TOrderBy} from "../components/Table/utils/shared.ts"
import {ELanguage, getCurrentLanguage} from "../i18n"
import {TReportsMetric} from "../queries/reports"
import {useUserSettingsQuery} from "../queries/user.ts"
import {TLeadsFiltering} from "../routes/Leads/useLeadsFiltering.ts"
import {TCompanyTableColumn} from "../routes/Prospects/Company/types.ts"
import {TAddingStageColumn, TSearchingStageColumn} from "../routes/Prospects/ProspectsFromLeads/AdvancedSearch/types.ts"
import {TAdminTableColumn} from "../routes/Prospects/shared/DataTable.tsx"
import {getFullName} from "../utils"
import {isCompanyUser} from "../utils/types"
import {
  AActivityEvents,
  AKlaraDashboardDealChipValues,
  AKlaraDashboardDealSortByValues,
  AKlaraDashboardDealViewValues,
  AKlaraDashboardGranularityValues,
  ALeadAutocompleteFields,
  Api,
  AProspectQueryStatusValues,
  ASalesAssistantDealPriorities,
  ASalesAssistantDealSortDirectionValues,
  ASalesCycleFilterStages,
  ASalesCycleProspectAutocompleteFields,
  ASalesCycleProspectStatuses,
  ASettings,
  AUsersListParamsRoleEnum,
  AUsersListParamsStatusEnum,
  AUserTypes,
  ContentType,
} from "./types.generated"

export const baseURL = import.meta.env.VITE_API_URL
const api = new Api({baseURL, withCredentials: true, headers: {Accept: ContentType.Json}})
api.instance.interceptors.request.use(config => ({...config, params: {lang: getCurrentLanguage(), ...config.params}}))

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 1000,
      throwOnError: (error, query) => {
        rollbar.error("Query error", error, query)
        return false
      },
    },
  },
})

export const prospectsGeneralKey = ["prospects"]
export const prospectsGeneralTablesKey = [...prospectsGeneralKey, "table"] as const
export const salesAssistantGeneralKey = ["sales assistant"] as const
export const klaraDashboardKey = ["klara dashboard"] as const
const prospectsSpecificTableKey =
  (table: string) =>
  ({
    salesCycleId,
    searchString,
    orderBy,
    assignmentId,
    status,
    segment,
  }: {
    salesCycleId: number
    assignmentId?: number
    searchString: string
    status: AProspectQueryStatusValues | null
    orderBy: TOrderBy<TAdminTableColumn | TCompanyTableColumn>
    segment?: string | null
  }) =>
  (paginationQuery: string) => [
    ...prospectsGeneralTablesKey,
    table,
    salesCycleId,
    assignmentId,
    searchString,
    orderBy,
    status,
    segment ?? null,
    paginationQuery,
  ]

export const leadsGeneralTableKey = ["leads"] as const
export const leadsImportersGeneralKey = ["leads importers"] as const

export const leadsAdvancedGeneralKey = ["leads advanced"] as const
export const leadsAdvancedStepGeneralKey = (sessionId: number) => ["leads advanced", sessionId]

export const activityFeedGeneralKey = ["activity feed"] as const

export const salesCycleDetailGeneralKey = ["sales cycle detail"] as const

export const saasUserManagementGeneralKey = ["saas user management"] as const

export const queryKey = {
  logout: ["logout"],
  userSettings: ["user settings"],
  masqueradeTargets: ["masquerade", "targets"],
  countries: ["countries"],
  stripe: (apiKey: string) => ["stripe", apiKey],
  checkoutDetail: (id: number) => ["checkout", id],
  checkoutPreview: (id: number) => ["checkout", "preview", id],
  checkoutUpdate: ["checkout", "update"],
  salesDashboard: (id: number) => ["sales dashboard", id],
  companyDashboard: (id: number) => ["company dashboard", id],
  companyDashboardActive: (id: number) => ["company dashboard", id, "active"],
  companyDashboardDraft: (id: number) => ["company dashboard", id, "draft"],
  companyDashboardFinished: (id: number) => ["company dashboard", id, "finished"],
  companyDashboardPaused: (id: number) => ["company dashboard", id, "paused"],
  salesCycles: ["sales cycles"],
  salesCycle: (id: number | undefined) => ["sales cycles", id],
  salesCycleDetail: (id: number) => [...salesCycleDetailGeneralKey, id],
  salesCycleNewsDetail: (id: number) => ["sales cycle news", id],
  salesCycleIterations: (filters: string) => (paginationQuery: string) => [
    "sales cycle iterations",
    filters,
    paginationQuery,
  ],
  salesCycleIteration: (id: string) => ["sales cycle iterations", id],
  salesCycleIterationMatchable: (id: string, searchString?: string) => [
    "sales cycle iterations",
    id,
    "matchable",
    searchString,
  ],
  company: (id: number) => ["company", id],
  companyWelcomeScreen: (id: number) => ["company welcome screen", id],
  salesperson: (id: number) => ["salesperson", id],
  salespersonPublic: (id: number) => ["salespersonPublic", id],
  salespersonBusinessInfo: (id: number) => ["salesperson business info", id],
  maintenanceCheck: ["maintenance check"],
  languages: ["languages"],
  prospects: (id: number) => (paginationQuery: string) => [...prospectsGeneralKey, id, paginationQuery],
  prospectsSalesCycle: (id?: number) => [...prospectsGeneralKey, "sales cycle", id].filter(part => part != null),
  prospectsTables: prospectsGeneralTablesKey,
  prospectsDraftTable: prospectsSpecificTableKey("draft"),
  prospectsLifetimeTable: prospectsSpecificTableKey("lifetime"),
  prospectsWaitingForApprovalTable: prospectsSpecificTableKey("waiting for approval"),
  prospectsFinishedTable: prospectsSpecificTableKey("finished"),
  saasDashboard: ["saas dashboard"],
  saasCompanyOnboardingStatus: () => ["saasCompanyOnboardingStatus"],
  saasSalesIntelligence: (startDate: string) => ["sales intelligence", startDate],
  saasSalesCyclesSalesIntelligenceIndexList: (startDate: string) => ["sales intelligence index", startDate],
  leads: (filter: TLeadsFiltering) => (paginationQuery: string) => [
    ...leadsGeneralTableKey,
    ...leadsFilterToQueryKey(filter),
    paginationQuery,
  ],
  removalRequestedLeads: (filter: TLeadsFiltering) => (paginationQuery: string) => [
    ...leadsGeneralTableKey,
    "removal requested",
    ...leadsFilterToQueryKey(filter),
    paginationQuery,
  ],
  leadsForAssignment: (assignmentId: number, filter: TLeadsFiltering) => (paginationQuery: string) => [
    ...leadsGeneralTableKey,
    assignmentId,
    ...leadsFilterToQueryKey(filter),
    paginationQuery,
  ],
  leadsAutocomplete: (field: ALeadAutocompleteFields, searchString: string) => [
    "leads autocomplete",
    field,
    searchString,
  ],
  leadDetail: (id: number | undefined) => ["lead detail", id],
  leadsImporters: (paginationQuery: string) => [...leadsImportersGeneralKey, paginationQuery],
  leadsMetadata: [...leadsGeneralTableKey, "metadata"],
  leadsAdvancedDraftedList: (sessionId: number, orderBy: TOrderBy<TSearchingStageColumn>) => [
    ...leadsAdvancedStepGeneralKey(sessionId),
    "drafted",
    orderBy,
  ],
  leadsAdvancedAddedList: (sessionId: number, orderBy: TOrderBy<TAddingStageColumn>) => [
    ...leadsAdvancedStepGeneralKey(sessionId),
    "added",
    orderBy,
  ],

  GDPR: (token: string) => ["GDPR", token],
  reportsSalesCycle: (salesCycleId: number) => ["reports", "sales cycle", salesCycleId],
  reportsIteration: (salesCycleId: number, iterationId: number | null) => [
    "reports",
    "sales cycle",
    salesCycleId,
    "iteration",
    iterationId,
  ],
  reportsNewsAndInsights: (salesCycleId: number, iterationId: number | null) => [
    "reports",
    "sales cycle",
    salesCycleId,
    "iteration",
    iterationId,
    "news and insights",
  ],
  reportsProspects:
    (
      salesCycleId: number,
      iterationId: number | null,
      assignmentId: string | null,
      filter: {
        searchString?: string
        segments?: string[]
        stages?: ASalesCycleFilterStages[]
        statuses?: ASalesCycleProspectStatuses[]
      }
    ) =>
    (paginationQuery: string) => [
      "reports",
      "sales cycle",
      salesCycleId,
      "iteration",
      iterationId,
      "assignment",
      assignmentId,
      "prospects",
      filter,
      paginationQuery,
    ],
  reportsProspectsAutocomplete: (
    salesCycleId: number,
    field: ASalesCycleProspectAutocompleteFields,
    searchString: string
  ) => ["reports", "sales cycle", salesCycleId, "prospects autocomplete", field, searchString],
  reportsAssignment: (salesCycleId: number, iterationId: number | null, assignmentId: string | null) => [
    "reports",
    "sales cycle",
    salesCycleId,
    "iteration",
    iterationId,
    "assignment",
    assignmentId,
  ],
  reportsChart: (
    metric: TReportsMetric | AActivityEvents,
    salesCycleId: number,
    iterationId: number | null,
    assignmentId: string | null
  ) => ["reports", "sales cycle", salesCycleId, "iteration", iterationId, "assignment", assignmentId, "chart", metric],
  activityFeed: (iterationId: number) => (paginationQuery: string) => [
    ...activityFeedGeneralKey,
    iterationId,
    paginationQuery,
  ],
  activityFeedProspect: (prospectId?: number, iterationId?: number) => [
    ...activityFeedGeneralKey,
    "prospect",
    prospectId,
    iterationId,
  ],
  dealNoteProspect: (prospectId?: number) => ["prospect deal notes", prospectId],
  salesCyclePeople: (salesCycleId: number) => ["sales cycle", salesCycleId, "people"],
  saasUserManagementMeta: [...saasUserManagementGeneralKey, "meta"],
  saasUserManagementData:
    (filter: {roles?: AUsersListParamsRoleEnum[]; searchString?: string; statuses?: AUsersListParamsStatusEnum[]}) =>
    (paginationQuery: string) => [...saasUserManagementGeneralKey, "data", filter, paginationQuery],
  customElevatorPitch: (salesCycleId: number | undefined) => ["custom elevator pitch", salesCycleId],
  salesAssistantDeals:
    (filter: {searchString?: string; priorities?: ASalesAssistantDealPriorities[]}) => (paginationQuery: string) => [
      ...salesAssistantGeneralKey,
      "deals",
      filter,
      paginationQuery,
    ],
  salesAssistantDealDetail: (externalId: string | undefined) => [...salesAssistantGeneralKey, "deal", externalId],
  salesAssistantSalesPeople: (filter: {sellingExternalId?: string}) => (paginationQuery: string) => [
    ...salesAssistantGeneralKey,
    "sales-people",
    filter,
    paginationQuery,
  ],
  klaraUpdateCenter: (filter: {
    search: string
    salesPersonExternalId: string | undefined
    dealId: number | undefined
    from: string | undefined
    to: string | undefined
  }) =>
    [
      ...klaraDashboardKey,
      "update center",
      {
        search: filter.search,
        salesPersonExternalId: filter.salesPersonExternalId ?? null,
        dealId: filter.dealId ?? null,
        from: filter.from ?? null,
        to: filter.to ?? null,
      },
    ] as const,
  klaraDealDetail: (dealId: number) => [...klaraDashboardKey, "deal", dealId] as const,
  klaraDeals: (filter: {
    from: string | undefined
    to: string | undefined
    view: AKlaraDashboardDealViewValues
    chip: AKlaraDashboardDealChipValues | undefined
    priorities: ASalesAssistantDealPriorities[]
    salesPersonExternalId: string | undefined
    search: string
    sortBy: AKlaraDashboardDealSortByValues | undefined
    sortDirection: ASalesAssistantDealSortDirectionValues | undefined
    page: number
    pageSize: number
  }) =>
    [
      ...klaraDashboardKey,
      "deals",
      {
        ...filter,
        from: filter.from ?? null,
        to: filter.to ?? null,
        chip: filter.chip ?? null,
        salesPersonExternalId: filter.salesPersonExternalId ?? null,
        sortBy: filter.sortBy ?? null,
        sortDirection: filter.sortDirection ?? null,
      },
    ] as const,
  klaraSummaries: [...klaraDashboardKey, "summary"] as const,
  klaraSummary: (range: {from: string | undefined; to: string | undefined}) =>
    [...klaraDashboardKey, "summary", {from: range.from ?? null, to: range.to ?? null}] as const,
  klaraCompanySettings: [...klaraDashboardKey, "company settings"] as const,
  klaraSalesPeople: (range: {from: string | undefined; to: string | undefined}) =>
    [...klaraDashboardKey, "sales people", {from: range.from ?? null, to: range.to ?? null}] as const,
  klaraUpdatesOverTime: (filter: {
    from: string | undefined
    to: string | undefined
    granularity: AKlaraDashboardGranularityValues
  }) =>
    [
      ...klaraDashboardKey,
      "updates over time",
      {from: filter.from ?? null, to: filter.to ?? null, granularity: filter.granularity},
    ] as const,
  reachCampaigns: (from: string | undefined, to: string | undefined) =>
    ["reach campaigns", from ?? null, to ?? null] as const,
  reachActivities: (from: string | undefined, to: string | undefined) =>
    ["reach activities", from ?? null, to ?? null] as const,
  reachOutreachDepth: (from: string | undefined, to: string | undefined) =>
    ["reach outreach depth", from ?? null, to ?? null] as const,
  reachResultsOverTime: (from: string | undefined, to: string | undefined) =>
    ["reach results over time", from ?? null, to ?? null] as const,
}

function leadsFilterToQueryKey(filter: TLeadsFiltering): QueryKey {
  return [
    {
      searchString: filter.searchString.valueDebounced,
      name: filter.name.valueDebounced,
      companyName: filter.companyName.valueDebounced,
      countries: filter.countries.valueDebounced.map(opt => opt.value),
      positions: filter.positions.valueDebounced.map(opt => opt.value),
      segments: filter.segments.valueDebounced.map(opt => opt.value),
      cities: filter.cities.valueDebounced.map(opt => opt.value),
      phone: filter.phone.valueDebounced,
      email: filter.email.valueDebounced,
      contactedByCompany: filter.contactedByCompany.valueDebounced,
      companySizes: filter.companySizes.valueDebounced.map(opt => opt.value),
      checkedRows: filter.checkedRowsForQuery,
      orderBy: filter.orderBy,
    },
  ]
}

const isSerializable = (obj: unknown): obj is Record<string, any> =>
  obj !== null && typeof obj === "object" && ["Object", "Array"].includes(obj.constructor.name)

/**
 * Rails requires a peculiar format for data in the multipart/form-data shape.
 * This function converts a regular object to a FormData in a shape that is acceptable by Rails.
 */
export function serializeForMultipart(source: Record<string, any>): FormData {
  const pairsTemp: Array<[string, any]> = []

  // This produces an array of key value pairs, where key looks like "[a][][c]"
  walkEntries(source, (key, value) => {
    if (value === undefined) {
      return
    }
    pairsTemp.push([key, value])
  })

  // this removes the brackets from the top level key, producing "a[][c]"
  const pairs: typeof pairsTemp = pairsTemp.map(([key, value]) => {
    const [, topLevelKey, restOfKeys] = key.match(/^\[(.*?)\](.*)$/) ?? []
    return [`${topLevelKey}${restOfKeys}`, value]
  })

  const formData = new FormData()
  pairs.forEach(([key, value]) => {
    formData.append(key, value)
  })

  return formData
}

/**
 * Calls append() on every leaf of the source object, with the key being the path to the leaf
 */
function walkEntries<T>(source: T, append: (key: string, value: any) => void) {
  if (!isSerializable(source)) {
    append("", source)
    return
  }

  if (Array.isArray(source)) {
    source.forEach(subSource => walkEntries(subSource, (subKey, subValue) => append(`[]${subKey}`, subValue)))
    return
  }

  Object.entries(source).forEach(([key, subSource]) =>
    walkEntries(subSource, (subKey, subValue) => append(`[${key}]${subKey}`, subValue))
  )
}

export type TRollbarUserInfo =
  | {
      id: -1
      username: "Not fetched yet"
      language: ELanguage
    }
  | {
      id: -2
      username: "Not logged in"
      language: ELanguage
    }
  | {
      id: number
      username: string
      email: string
      type: AUserTypes
      language: ELanguage
      isMasqueraded: boolean
    }

// @ts-expect-error __COMMIT_SHA is coming from Vite config
const commitSHA = __COMMIT_SHA

const getRollbarPerson = (): TRollbarUserInfo => {
  const settingsQuery = queryClient.getQueryState<ASettings>(queryKey.userSettings)
  const data = settingsQuery?.data

  if (settingsQuery?.status === "success" && data) {
    const username = isCompanyUser(data.user) ? data.user.company.name : getFullName(data.user)

    return {
      id: data.user.id,
      username,
      type: data.user.type,
      email: data.user.email,
      language: getCurrentLanguage(),
      isMasqueraded: data.masqueraded ?? false,
    }
  }

  if (settingsQuery?.status === "error") {
    return {id: -2, username: "Not logged in", language: getCurrentLanguage()}
  }

  return {id: -1, username: "Not fetched yet", language: getCurrentLanguage()}
}

export const rollbar = new Rollbar({
  accessToken: import.meta.env.VITE_ROLLBAR_TOKEN,
  environment: import.meta.env.VITE_ENVIRONMENT,
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    client: {
      javascript: {source_map_enabled: true, code_version: commitSHA},
    },
  },
  transform: data => {
    data.smartlookUrl = (globalThis as {smartlook?: {playUrl?: string}})?.smartlook?.playUrl
    data.person = getRollbarPerson()
  },
})

/**
 * Buffers survicate commands until the service is initialized
 */
class SurvicateBuffer {
  private instance: Survicate | null = null
  private queue: Array<[keyof Survicate, Parameters<Survicate[keyof Survicate]>]> = []

  constructor() {
    this.initialize()
  }

  public run = <TCommand extends keyof Survicate>(command: TCommand, ...args: Parameters<Survicate[TCommand]>) => {
    if (!this.instance) {
      this.queue.push([command, args])
      return
    }

    this.executeCommand(command, args)
  }

  private initialize = () => {
    if (!import.meta.env.VITE_SURVICATE_KEY) {
      return
    }

    initSurvicate({
      workspaceKey: import.meta.env.VITE_SURVICATE_KEY,
    }).then(() => {
      this.instance = getSurvicateInstance()
      this.executeQueue()
    })
  }

  private executeCommand = <TCommand extends keyof Survicate>(
    command: TCommand,
    args: Parameters<Survicate[TCommand]>
  ) => {
    // @ts-expect-error TS just basically gives up here
    this.instance?.[command]?.(...args)
  }

  private executeQueue = () => {
    this.queue.forEach(([command, args]) => this.executeCommand(command, args))
    this.queue = []
  }
}

export const survicate = new SurvicateBuffer()

export const ServicesUserSetup: React.FC = () => {
  const {data, error} = useUserSettingsQuery()

  React.useEffect(() => {
    if (!error) {
      return
    }

    survicate.run("setVisitorTraits", getRollbarPerson())
    survicate.run("retarget")
  }, [error])

  React.useEffect(() => {
    if (!data) {
      return
    }

    survicate.run("setVisitorTraits", getRollbarPerson())
    survicate.run("retarget")
  }, [data])

  return null
}

export function multipartRequest({
  path,
  data,
  method,
}: {
  path: string
  data: Record<string, any>
  method: "POST" | "PUT" | "PATCH"
}) {
  return axios.request({
    url: `${import.meta.env.VITE_API_URL}${path}`,
    params: {lang: getCurrentLanguage()},
    headers: {Accept: "application/json"},
    // Axios also has methods that serialize the values for you, but they produce obj[1][key] instead of obj[][key]
    // for objects inside an array. Our Rails BE cannot process that.
    data: serializeForMultipart(data),
    method,
    withCredentials: true,
  })
}

export default api
